import React from 'react'
import { useAppDispatch, useAppSelector } from '../redux'

import { GroupType } from '@SonarCX/slices/customFields/groups/helpers'

import {
  selectCustomFieldGroups,
  fetchCustomFieldGroup
} from '@SonarCX/slices/customFields/groups'
import { uniqueArray } from '@SonarCX/utilities'

const useCustomFieldGroups = (
  groupType: GroupType,
  filterType: string = ''
) => {
  const dispatch = useAppDispatch()

  const { groups, loaded, loading } = useAppSelector(selectCustomFieldGroups)

  // All groups of the specified type
  const allGroups = React.useMemo(
    () => groups[groupType] ?? [],
    [groups, groupType]
  )

  // Filtered and sorted groups based on type id
  const filteredGroups = React.useMemo(() => {
    const orderSort = (a: APICustomFieldGroup, b: APICustomFieldGroup) =>
      a.order - b.order

    const general = allGroups
      .filter(group => group.type_id === null)
      .sort(orderSort)
    const specific = allGroups
      .filter(group => group.type_id === filterType)
      .sort(orderSort)

    return uniqueArray(general, specific) as APICustomFieldGroup[]
  }, [allGroups, filterType])

  // Check if the current group has fields to show
  const hasFields = React.useMemo(() => {
    const count = filteredGroups.reduce(
      (acc, curr) => acc + curr.field_ids.length,
      0
    )

    return count > 0
  }, [filteredGroups])

  // Load group if needed
  React.useEffect(() => {
    if (!loaded[groupType] && !loading[groupType]) {
      dispatch(fetchCustomFieldGroup(groupType))
    }
  }, [loaded, loading, groupType, dispatch])

  return { allGroups, groups: filteredGroups, hasFields }
}

export default useCustomFieldGroups
